// AutomationPreview.tsx
// Simple, clear automation preview focusing on WHEN and THEN logic

import React, { useEffect, useState } from 'react';
import PopupComponent from '../../components/PopupComponent';
import { AutomationPreviewGenerator } from './AutomationPreviewGenerator';
import logger from '../../utils/logger';

interface AutomationPreviewProps {
  isVisible: boolean;
  onClose: () => void;
  automationData: any;
  dayActions: any;
   onConfirm: () => void;
  dayDelays: any;
  fieldConditions: any[];
  timeOffsetConfig: any;
  isTimeBasedEvent: boolean;
  sourceData?: any[];
  statusLabelData?: any[];
  taskData?: any[];
  userListData?: any[];
  leadListData?: any[];
  showEditButton?: boolean;
  onEdit?: () => void;
}

const AutomationPreview: React.FC<AutomationPreviewProps> = ({
  isVisible,
  onClose,
  automationData,
  dayActions,
  dayDelays,
  fieldConditions,
  timeOffsetConfig,
  isTimeBasedEvent,
  sourceData = [],
  onConfirm,
  statusLabelData = [],
  taskData = [],
  userListData = [],
  leadListData = [],
  showEditButton = true,
  onEdit
}) => {
  const [previewData, setPreviewData] = useState<any>(null);

  useEffect(() => {
    if (isVisible) {
      logger.log('[AutomationPreview] Generating simple preview...');
      generateSimplePreview();
    }
  }, [isVisible, automationData, dayActions, dayDelays, fieldConditions]);

  /**
   * Generate a simple, clear preview focusing on WHEN and THEN
   */
  const generateSimplePreview = () => {
    try {
      logger.log('[AutomationPreview] Creating simple preview structure...');
      
      // Create generator for complex logic but extract simple data
      const generator = new AutomationPreviewGenerator(
        automationData,
        dayActions,
        dayDelays,
        fieldConditions,
        timeOffsetConfig,
        isTimeBasedEvent,
        sourceData,
        statusLabelData,
        taskData,
        userListData,
        leadListData
      );

      const fullPreview = generator.generate();
      
      // Simplify the data for clearer display
      const simplePreview = {
        trigger: fullPreview.trigger,
        conditions: fullPreview.conditions,
      actionGroups: generateActionGroups(), // CHANGED: Use actionGroups instead of actions
        totalActions: Object.values(dayActions).flat().length
      };

      logger.log('[AutomationPreview] Simple preview generated:', simplePreview);
      setPreviewData(simplePreview);
    } catch (error) {
      console.error('[AutomationPreview] Error generating simple preview:', error);
     setPreviewData({
  trigger: 'Error generating preview',
  conditions: '',
  actionGroups: [], // CHANGED: Use actionGroups instead of actions
  totalActions: 0
});
    }
  };

/**
 * Generate action groups by day with proper timing - FIXED FOR DAY GROUPING
 */
const generateActionGroups = () => {
  logger.log('[AutomationPreview] generateActionGroups - Starting with dayActions:', dayActions);
  logger.log('[AutomationPreview] generateActionGroups - dayDelays:', dayDelays);
  
  const actionGroups: Array<{
    timing: string;
    actions: Array<{ description: string; }>
  }> = [];

  // Sort days and process actions
  const sortedDays = Object.entries(dayActions)
    .sort(([a], [b]) => parseInt(a.split('_')[1]) - parseInt(b.split('_')[1]));

  logger.log('[AutomationPreview] generateActionGroups - sortedDays:', sortedDays.map(([key, actions]) => `${key}: ${(actions as any[]).length} actions`));

  sortedDays.forEach(([dayKey, dayActionList], dayIndex) => {
    const dayNum = parseInt(dayKey.split('_')[1]);
    const typedDayActionList = dayActionList as any[];
    logger.log(`[AutomationPreview] Processing day ${dayNum} (${dayKey}) with ${typedDayActionList.length} actions`);
    
   // Use actual day number from API (day_0 = Day 1, day_1 = Day 2, etc.)
const actualDayNumber = dayNum + 1; // Convert API day index to user-friendly day number
let dayTiming = `Day ${actualDayNumber}`;
logger.log(`[AutomationPreview] Day ${dayNum} (API) -> Day ${actualDayNumber} (UI): Set timing to "${dayTiming}"`);

    // Group all actions for this day
    const dayActions_processed = typedDayActionList.map((action, actionIndex) => {
      const description = getSimpleActionDescription(action);
      logger.log(`[AutomationPreview] Day ${dayNum}, Action ${actionIndex + 1}: ${description}`);
      return { description };
    });

    // Add the group for this day
    actionGroups.push({
      timing: dayTiming,
      actions: dayActions_processed
    });
  });

  logger.log('[AutomationPreview] generateActionGroups - Final groups:', actionGroups);
  return actionGroups;
};

  /**
   * Get simple, clear action descriptions
   */
  const getSimpleActionDescription = (action: any): string => {
    const actionType = action.type;
    const actionData = action.data || {};

    switch (actionType) {
      case 'send_whatsapp':
        if (actionData.templateData?.name) {
          return `📱 Send WhatsApp: "${actionData.templateData.name}"`;
        }
        return '📱 Send WhatsApp message';

     case 'send_email':
  if (actionData.templateData?.details?.title) {
    return `📧 Send Email: "${actionData.templateData.details.title}"`;
  }
  return '📧 Send Email';
  
case 'send_gmail':
  if (actionData.templateData?.details?.title) {
    return `📧 Send Gmail: "${actionData.templateData.details.title}"`;
  }
  return '📧 Send Gmail';
  
case 'send_zeptomail':
  if (actionData.templateData?.details?.title) {
    return `📧 Send ZeptoMail: "${actionData.templateData.details.title}"`;
  }
  return '📧 Send ZeptoMail';

      case 'create_task':
        let taskDesc = '✅ Create task';
        if (actionData.type) {
          const taskType = taskData.find(t => t.value === actionData.type);
          if (taskType) {
            taskDesc += `: ${taskType.name}`;
          }
        }
        if (actionData.assignedTo?.length > 0) {
          const userId = Array.isArray(actionData.assignedTo) ? actionData.assignedTo[0] : actionData.assignedTo;
          const user = userListData.find(u => u._id === userId);
          if (user) {
            taskDesc += ` (assigned to ${user.firstName})`;
          }
        }
        return taskDesc;

      case 'status_change':
        if (actionData.status) {
          const status = statusLabelData.find(s => s.value === actionData.status);
          const statusName = status ? status.name : actionData.status;
          return `🔄 Change status to: ${statusName}`;
        }
        return '🔄 Change lead status';

      case 'label_change':
        if (actionData.label?.length > 0) {
          const labelValue = Array.isArray(actionData.label) ? actionData.label[0] : actionData.label;
          const label = statusLabelData.find(l => l.value === labelValue);
          const labelName = label ? label.name : labelValue;
          return `🏷️ Add label: ${labelName}`;
        }
        return '🏷️ Change lead label';

      case 'assign_lead':
        if (actionData.assignToUser) {
          const user = userListData.find(u => u._id === actionData.assignToUser);
          const userName = user ? user.firstName : 'Unknown User';
          return `👤 Assign lead to: ${userName}`;
        }
        return '👤 Assign lead';

case 'push_notification':
  if (actionData.push_notification) {
    const user = userListData.find(u => u._id === actionData.push_notification);
    const userName = user ? user.firstName : 'Unknown User';
    return `🔔 Notify: ${userName}`;
  }
  return '🔔 Send notification';

      case 'move_to_list':
      case 'copy_to_list':
        const actionText = actionType === 'move_to_list' ? 'Move' : 'Copy';
        if (actionData.targetListId) {
          const list = leadListData.find(l => l._id === actionData.targetListId);
          const listName = list ? list.name : 'Unknown List';
          return `📋 ${actionText} to: ${listName}`;
        }
        return `📋 ${actionText} lead`;

      case 'add_to_google_sheet':
        return '📊 Add to Google Sheet';

      case 'add_facebook_audience':
        if (actionData.audienceName) {
          return `📘 Add to Facebook audience: ${actionData.audienceName}`;
        }
        return '📘 Add to Facebook audience';

      case 'webhook':
        if (actionData.webhookUrl) {
          try {
            const url = new URL(actionData.webhookUrl);
            const method = actionData.requestMethod || 'POST';
            return `🔗 ${method} webhook to ${url.hostname}`;
          } catch {
            const method = actionData.requestMethod || 'POST';
            return `🔗 ${method} webhook`;
          }
        }
        return '🔗 Trigger webhook';

      default:
        return `⚙️ ${actionType.replace('_', ' ')}`;
    }
  };

  /**
   * Handle edit button click
   */
  const handleEditClick = () => {
    logger.log('[AutomationPreview] Edit clicked');
    if (onEdit) onEdit();
    onClose();
  };

  /**
   * Render the simple preview content
   */
  const renderSimplePreview = () => {
  if (!previewData) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Generating preview...</p>
      </div>
    );
  }

  // Use the complex generator's data but simplify the display
  const generator = new AutomationPreviewGenerator(
    automationData,
    dayActions,
    dayDelays,
    fieldConditions,
    timeOffsetConfig,
    isTimeBasedEvent,
    sourceData,
    statusLabelData,
    taskData,
    userListData,
    leadListData
  );
  
  const fullPreview = generator.generate();
  logger.log('[AutomationPreview] Using full generator data:', fullPreview);

  return (
    <div style={{ 
      fontFamily: '"Gilroy-Regular", sans-serif',
      color: '#1a202c',
      lineHeight: '1.6'
    }}>
      {/* Automation Name */}
      <div style={{
        textAlign: 'center',
        marginBottom: '24px',
        padding: '16px',
        backgroundColor: '#f0f9ff',
        borderRadius: '8px'
      }}>
        <h3 style={{
          margin: '0 0 8px 0',
          fontSize: '20px',
          color: '#1976d2',
          fontFamily: '"Gilroy-SemiBold", sans-serif'
        }}>
          🤖 {automationData.name}
        </h3>
        {automationData.description && (
          <p style={{
            margin: '0',
            fontSize: '14px',
            color: '#64748b'
          }}>
            {automationData.description}
          </p>
        )}
      </div>

    {/* WHEN Section - Simple and Clean */}
<div style={{
  marginBottom: '24px',
  padding: '16px',
  backgroundColor: '#f0fdf4',
  border: '1px solid #bbf7d0',
  borderRadius: '8px',
  borderLeft: '4px solid #10b981'
}}>
  <h4 style={{
    margin: '0 0 12px 0',
    fontSize: '16px',
    color: '#059669',
    fontFamily: '"Gilroy-SemiBold", sans-serif',
    display: 'flex',
    alignItems: 'center'
  }}>
    🎯 WHEN this happens:
  </h4>
  <p style={{
    margin: '0',
    fontSize: '15px',
    color: '#1a202c',
    fontWeight: '500'
  }}>
    {(() => {
      // Simple trigger text based on event type
      const eventType = automationData.event;
      
      if (eventType === 'task_due_date') {
        const amount = timeOffsetConfig.amount || 1;
        const operation = timeOffsetConfig.operation || 'before';
        const taskType = automationData.targetValues === 'any' ? 'any task' : 'demo task';
        return `${amount} day${amount > 1 ? 's' : ''} ${operation} ${taskType} is due`;
      }
      
      if (eventType === 'task_completed') {
        const amount = timeOffsetConfig.amount || 1;
        const operation = timeOffsetConfig.operation || 'after';
        const taskType = automationData.targetValues === 'any' ? 'any task' : 'demo task';
        return `${amount} day${amount > 1 ? 's' : ''} ${operation} ${taskType} is completed`;
      }
      
      // For all other events, use simple descriptions
      const eventNames = {
        'create_lead': 'A new lead is created',
        'edit_lead': 'Lead information is updated',
        'status_change': 'Lead status changes',
        'label_change': 'Lead label changes',
        'create_quotation': 'A new quotation is created',
        'edit_quotation': 'A quotation is updated',
        'create_invoice': 'A new invoice is created',
        'edit_invoice': 'An invoice is updated',
        'create_order': 'A new order is created',
        'edit_order': 'An order is updated',
        'create_task': 'A new task is created',
        'edit_task': 'A task is updated',
        'create_activity': 'A new activity is logged',
        'call_duration': 'A call meets duration criteria',
        'call_count': 'Call count reaches threshold',
        'new_lead_added': 'A lead is added to a list'
      };
      
      return eventNames[eventType as keyof typeof eventNames] || `${eventType} event occurs`;
    })()}
  </p>
</div>

      {/* THEN Section - Use generator's timeline but group by day */}
      <div style={{
        marginBottom: '24px',
        padding: '16px',
        backgroundColor: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: '8px',
        borderLeft: '4px solid #3b82f6'
      }}>
        <h4 style={{
          margin: '0 0 16px 0',
          fontSize: '16px',
          color: '#1e40af',
          fontFamily: '"Gilroy-SemiBold", sans-serif',
          display: 'flex',
          alignItems: 'center'
        }}>
          ⚡ THEN do this:
        </h4>
        
        {fullPreview.timeline.map((dayGroup: any, groupIndex: number) => (
          <div key={groupIndex} style={{
            marginBottom: '20px',
            padding: '16px',
            backgroundColor: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
          }}>
            {/* Day Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '12px',
              paddingBottom: '8px',
              borderBottom: '1px solid #f1f5f9'
            }}>
              <div style={{
                fontSize: '14px',
                color: '#3b82f6',
                fontWeight: '600',
                backgroundColor: '#dbeafe',
                padding: '6px 12px',
                borderRadius: '6px',
                fontFamily: '"Gilroy-SemiBold", sans-serif'
              }}>
                {dayGroup.dayLabel}
              </div>
            </div>
            
            {/* Actions for this day */}
            {dayGroup.actions.map((action: string, actionIndex: number) => (
              <div key={actionIndex} style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 12px',
                marginBottom: actionIndex < dayGroup.actions.length - 1 ? '8px' : '0',
                backgroundColor: '#f8fafc',
                borderRadius: '4px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{
                  width: '6px',
                  height: '6px',
                  backgroundColor: '#3b82f6',
                  borderRadius: '50%',
                  marginRight: '12px',
                  flexShrink: 0
                }}></div>
                <div style={{
                  fontSize: '14px',
                  color: '#374151',
                  fontWeight: '500',
                  fontFamily: '"Gilroy-Medium", sans-serif'
                }}>
                  {action}
                </div>
              </div>
            ))}
            
            {/* Show delay if exists */}
            {dayGroup.delayText && (
              <div style={{
                marginTop: '12px',
                padding: '8px 12px',
                backgroundColor: '#fef3c7',
                border: '1px solid #fcd34d',
                borderRadius: '4px',
                fontSize: '13px',
                color: '#d97706',
                fontWeight: '500'
              }}>
                ⏱️ {dayGroup.delayText}
              </div>
            )}
          </div>
        ))}
      </div>

    {/* Summary - Simple and Clean */}
<div style={{
  padding: '16px',
  backgroundColor: '#faf5ff',
  border: '1px solid #e9d5ff',
  borderRadius: '8px',
  borderLeft: '4px solid #8b5cf6'
}}>
  <h4 style={{
    margin: '0 0 8px 0',
    fontSize: '16px',
    color: '#7c3aed',
    fontFamily: '"Gilroy-SemiBold", sans-serif'
  }}>
    📋 Summary
  </h4>
  <p style={{
    margin: '0',
    fontSize: '14px',
    color: '#374151'
  }}>
    {(() => {
      const totalActions = Object.values(dayActions).flat().length;
      const totalDays = Object.keys(dayActions).length;
      
      if (totalDays === 1) {
        return `This automation will execute ${totalActions} action${totalActions !== 1 ? 's' : ''} immediately when triggered.`;
      } else {
        const dayNumbers = Object.keys(dayActions)
          .map(key => parseInt(key.split('_')[1]))
          .sort((a, b) => a - b);
        const lastDay = Math.max(...dayNumbers);
        
        return `This automation will execute ${totalActions} action${totalActions !== 1 ? 's' : ''} across ${totalDays} day${totalDays !== 1 ? 's' : ''} (Day ${dayNumbers.join(', Day ')}).`;
      }
    })()}
  </p>
</div>
    </div>
  );
};

  /**
   * Render footer actions
   */
  const renderFooterActions = () => {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
        paddingTop: '16px',
        borderTop: '1px solid #e2e8f0'
      }}>
        {showEditButton && (
          <button 
            type="button"
            onClick={handleEditClick}
            style={{
              background: 'transparent',
              color: '#3b82f6',
              border: '1px solid #3b82f6',
              padding: '8px 20px',
              borderRadius: '6px',
              fontFamily: '"Gilroy-Medium", sans-serif',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            ✏️ Edit Automation
          </button>
        )}
        <button 
          type="button"
          onClick={onConfirm}
          style={{
            background: '#10b981',
            color: '#ffffff',
            border: '1px solid #10b981',
            padding: '10px 24px',
            borderRadius: '6px',
            fontFamily: '"Gilroy-SemiBold", sans-serif',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          👍 Looks Good
        </button>
      </div>
    );
  };

  if (!isVisible) {
    return null;
  }

  return (
    <PopupComponent
      isOpen={isVisible}
      onClose={onClose}
      title="🔍 Automation Preview"
      size="lg"
      footer={renderFooterActions()}
    >
      {renderSimplePreview()}
    </PopupComponent>
  );
};

export default AutomationPreview;